import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
// Custom CountUp component for the savings numbers
function CountUp({
  end,
  prefix = '',
  suffix = ''
}: {
  end: number;
  prefix?: string;
  suffix?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const count = useMotionValue(0);
  const rounded = useTransform(count, latest => Math.round(latest).toLocaleString());
  useEffect(() => {
    if (isInView) {
      animate(count, end, {
        duration: 2,
        ease: 'easeOut'
      });
    }
  }, [isInView, count, end]);
  return <motion.span ref={ref} data-id="element-146">
      {prefix}
      <motion.span data-id="element-147">{rounded}</motion.span>
      {suffix}
    </motion.span>;
}
export function RepairExamples() {
  return <section id="examples" className="py-20 px-4 bg-brand-light" data-id="element-148">
      <div className="max-w-6xl mx-auto" data-id="element-149">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-150">
          <span className="uppercase tracking-widest text-sm font-bold text-brand-accent mb-4 block" data-id="element-151">
            Real repair examples
          </span>
          <h2 className="font-heading uppercase tracking-wide text-3xl md:text-4xl lg:text-5xl font-extrabold text-brand-dark mb-6" data-id="element-152">
            What accurate diagnosis can save
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-153">
          {/* Case 1 */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden flex flex-col" data-id="element-154">
            <div className="bg-gray-50 px-8 py-6 border-b border-gray-200" data-id="element-155">
              <h3 className="font-heading uppercase tracking-wide text-2xl font-bold text-brand-dark mb-1" data-id="element-156">
                2018 Ford Escape
              </h3>
              <p className="text-gray-500 font-medium" data-id="element-157">Turbo underboost code</p>
            </div>

            <div className="p-8 flex-grow flex flex-col" data-id="element-158">
              <div className="space-y-6 mb-8 flex-grow" data-id="element-159">
                <div data-id="element-160">
                  <p className="text-sm text-gray-500 uppercase tracking-wider font-bold mb-1" data-id="element-161">
                    Dealership recommendation
                  </p>
                  <div className="flex justify-between items-end border-b border-gray-100 pb-2" data-id="element-162">
                    <span className="text-lg text-gray-700" data-id="element-163">
                      Turbocharger replacement
                    </span>
                    <span className="text-lg text-gray-400 line-through font-medium" data-id="element-164">
                      $4,000+
                    </span>
                  </div>
                </div>

                <div data-id="element-165">
                  <p className="text-sm text-brand-accent uppercase tracking-wider font-bold mb-1" data-id="element-166">
                    Lancer repair
                  </p>
                  <div className="flex justify-between items-end border-b border-gray-100 pb-2" data-id="element-167">
                    <span className="text-lg font-bold text-brand-dark" data-id="element-168">
                      Charge pipe replacement
                    </span>
                    <span className="text-lg font-bold text-brand-dark" data-id="element-169">
                      $700
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-xl p-6 mb-8 text-center border border-emerald-100" data-id="element-170">
                <p className="text-emerald-800 font-bold uppercase tracking-wider text-sm mb-2" data-id="element-171">
                  Customer savings
                </p>
                <p className="text-4xl md:text-5xl font-extrabold text-brand-success" data-id="element-172">
                  <CountUp end={3300} prefix="$" suffix="+" data-id="element-173" />
                </p>
              </div>

              <p className="text-gray-600 leading-relaxed" data-id="element-174">
                Our technicians found a cracked charge pipe leaking boost
                pressure — the real cause of the underboost code. Replacing the
                pipe restored full turbo performance. The turbocharger was never
                needed.
              </p>
            </div>
          </motion.div>

          {/* Case 2 */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden flex flex-col" data-id="element-175">
            <div className="bg-gray-50 px-8 py-6 border-b border-gray-200" data-id="element-176">
              <h3 className="font-heading uppercase tracking-wide text-2xl font-bold text-brand-dark mb-1" data-id="element-177">
                Lincoln Sedan
              </h3>
              <p className="text-gray-500 font-medium" data-id="element-178">
                Electronic parking brake fault
              </p>
            </div>

            <div className="p-8 flex-grow flex flex-col" data-id="element-179">
              <div className="space-y-6 mb-8 flex-grow" data-id="element-180">
                <div data-id="element-181">
                  <p className="text-sm text-gray-500 uppercase tracking-wider font-bold mb-1" data-id="element-182">
                    Dealership recommendation
                  </p>
                  <div className="flex justify-between items-end border-b border-gray-100 pb-2" data-id="element-183">
                    <span className="text-lg text-gray-700" data-id="element-184">
                      Full wiring harness replacement
                    </span>
                    <span className="text-lg text-gray-400 line-through font-medium" data-id="element-185">
                      $3,000
                    </span>
                  </div>
                </div>

                <div data-id="element-186">
                  <p className="text-sm text-brand-accent uppercase tracking-wider font-bold mb-1" data-id="element-187">
                    Lancer repair
                  </p>
                  <div className="flex justify-between items-end border-b border-gray-100 pb-2" data-id="element-188">
                    <span className="text-lg font-bold text-brand-dark" data-id="element-189">
                      Targeted wiring repair
                    </span>
                    <span className="text-lg font-bold text-brand-dark" data-id="element-190">
                      $543
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-xl p-6 mb-8 text-center border border-emerald-100" data-id="element-191">
                <p className="text-emerald-800 font-bold uppercase tracking-wider text-sm mb-2" data-id="element-192">
                  Customer savings
                </p>
                <p className="text-4xl md:text-5xl font-extrabold text-brand-success" data-id="element-193">
                  <CountUp end={2400} prefix="$" suffix="+" data-id="element-194" />
                </p>
              </div>

              <p className="text-gray-600 leading-relaxed" data-id="element-195">
                We traced the fault to a damaged section of wiring near the
                parking brake actuator. Instead of replacing the entire harness,
                we repaired the affected section and resolved the issue
                completely.
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center mt-16" data-id="element-196">
          <a href="tel:6512240267" className="inline-flex justify-center items-center px-8 py-4 bg-brand-accent hover:bg-brand-accentHover text-white font-bold rounded-md transition-colors shadow-lg shadow-brand-accent/20" data-id="element-197">
            Get a Second Opinion
          </a>
          <p className="text-gray-500 text-sm mt-3" data-id="element-198">
            Schedule an inspection and verify the repair before replacing
            expensive parts.
          </p>
        </motion.div>
      </div>
    </section>;
}